import React, { useState } from 'react'
import {
  Button,
  Flex,
  Input,
  Loading,
  Text,
  Timeline,
  useAppTheme,
} from '@workwhile/ui'
import { parseErrorMessage } from 'api'
import { useListingFunnelEventsQuery } from 'queries/listings/useListingFunnelEventsQuery'
import { useShiftDetailContextValue } from '../../../../context/ShiftDetailProvider'
import { formatDistanceToNowStrict } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'

type Props = {
  listingId: number
  timeRange: { startDate: string; endDate: string }
}

export const WorkerEventTimeline = ({ listingId, timeRange }: Props) => {
  const { colors } = useAppTheme()
  const [workerIdInput, setWorkerIdInput] = useState('')
  const [selectedWorkerId, setSelectedWorkerId] = useState<number | null>(null)

  const {
    state: {
      location: {
        address: { timezone },
      },
    },
  } = useShiftDetailContextValue()

  const {
    data: eventsData,
    isLoading,
    isError,
    error,
  } = useListingFunnelEventsQuery(listingId, selectedWorkerId, timeRange)

  const parsedError = parseErrorMessage(error)

  const handleViewWorkerTimeline = () => {
    const workerId = Number(workerIdInput)
    if (!Number.isInteger(workerId) || workerId <= 0) {
      return
    }
    setSelectedWorkerId(workerId)
  }

  const handleClearWorkerTimeline = () => {
    setSelectedWorkerId(null)
    setWorkerIdInput('')
  }

  return (
    <>
      <Flex alignItems="center" gap={2} flexWrap="wrap" mb={2}>
        <Input
          placeholder="Worker ID"
          type="number"
          value={workerIdInput}
          onChange={(e) => setWorkerIdInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleViewWorkerTimeline()
            }
          }}
          style={{ maxWidth: 160 }}
        />
        <Button variant="primary" onClick={handleViewWorkerTimeline}>
          View timeline
        </Button>
        {selectedWorkerId ? (
          <Button variant="secondary" onClick={handleClearWorkerTimeline}>
            Clear
          </Button>
        ) : null}
      </Flex>

      {!selectedWorkerId ? (
        <Text fontSize={2} color="lightText">
          Enter a worker ID to see their funnel events for this listing.
        </Text>
      ) : isLoading ? (
        <Flex minHeight={120} justifyContent="center" alignItems="center">
          <Loading />
        </Flex>
      ) : isError ? (
        <Text color={colors.errors[200]} fontSize={2}>
          {parsedError ?? 'Failed to load worker timeline.'}
        </Text>
      ) : !eventsData?.events?.length ? (
        <Text fontSize={2} color="lightText">
          No events found for worker {selectedWorkerId}.
        </Text>
      ) : (
        <>
          <Text fontSize={1} color="lightText" mb={2}>
            Worker {selectedWorkerId} · {eventsData.events.length} event
            {eventsData.events.length === 1 ? '' : 's'}
          </Text>
          <Timeline>
            {eventsData.events.map((event, index) => (
              <Timeline.Item
                key={`${event.rawEventType}-${event.eventTime}-${index}`}
                color={colors.primaries[200]}
              >
                <Text color="lightText" fontSize={1}>
                  {`${formatInTimeZone(new Date(event.eventTime), timezone, 'MMM d, h:mm a z')} (${formatDistanceToNowStrict(new Date(event.eventTime), { addSuffix: true })})`}
                </Text>
                <Text fontSize={2}>{event.name}</Text>
              </Timeline.Item>
            ))}
          </Timeline>
        </>
      )}
    </>
  )
}
